<template>
  <div
    v-on-clickaway="closePanel"
    class="flex flex-row items-center gap-1.5 relative"
  >
    <span
      class="tab-customize-button"
      :class="{ active: isCustomizePanelOpen, disabled: disabledSwitchGroup }"
      @click="togglePanel"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M17.6533 19.6558C16.7164 19.6558 15.9245 19.3323 15.2775 18.6855C14.6307 18.0385 14.3073 17.2466 14.3073 16.3098C14.3073 15.3728 14.6307 14.5809 15.2775 13.934C15.9245 13.287 16.7164 12.9635 17.6533 12.9635C18.5903 12.9635 19.3822 13.287 20.029 13.934C20.676 14.5809 20.9995 15.3728 20.9995 16.3098C20.9995 17.2466 20.676 18.0385 20.029 18.6855C19.3822 19.3323 18.5903 19.6558 17.6533 19.6558ZM17.6523 18.1558C18.1619 18.1558 18.5973 17.9757 18.9583 17.6155C19.3191 17.2552 19.4995 16.8203 19.4995 16.3108C19.4995 15.8011 19.3194 15.3658 18.959 15.0048C18.5989 14.6439 18.1639 14.4635 17.6543 14.4635C17.1446 14.4635 16.7094 14.6437 16.3485 15.004C15.9877 15.3642 15.8073 15.7991 15.8073 16.3088C15.8073 16.8184 15.9874 17.2537 16.3475 17.6145C16.7079 17.9754 17.1428 18.1558 17.6523 18.1558ZM4.49953 17.0598V15.5598H12.115V17.0598H4.49953ZM6.34578 11.0405C5.40878 11.0405 4.61686 10.717 3.97003 10.07C3.32303 9.42318 2.99953 8.63127 2.99953 7.69427C2.99953 6.75743 3.32303 5.96552 3.97003 5.31852C4.61686 4.67168 5.40878 4.34827 6.34578 4.34827C7.28261 4.34827 8.07453 4.67168 8.72153 5.31852C9.36836 5.96552 9.69178 6.75743 9.69178 7.69427C9.69178 8.63127 9.36836 9.42318 8.72153 10.07C8.07453 10.717 7.28261 11.0405 6.34578 11.0405ZM6.34478 9.54052C6.85444 9.54052 7.28969 9.36035 7.65053 9.00002C8.01136 8.63985 8.19178 8.20493 8.19178 7.69527C8.19178 7.1856 8.01169 6.75035 7.65153 6.38952C7.29119 6.02868 6.85628 5.84827 6.34678 5.84827C5.83711 5.84827 5.40178 6.02835 5.04078 6.38852C4.67994 6.74885 4.49953 7.18377 4.49953 7.69327C4.49953 8.20293 4.67969 8.63827 5.04003 8.99927C5.40019 9.3601 5.83511 9.54052 6.34478 9.54052ZM11.884 8.44427V6.94427H19.4995V8.44427H11.884Z"
          fill="#6B7280"
        />
      </svg>
    </span>
    <div class="tabs-carousel">
      <transition name="carousel-fade">
        <button
          v-if="canScrollLeft"
          class="carousel-arrow carousel-arrow--left"
          @click="scrollTabs(-1)"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </transition>

      <div
        ref="tabsContainer"
        class="tabs-scroll-container"
        @scroll="updateScrollState"
      >
        <div
          v-for="item in activeTabItems"
          :key="item.key"
          class="tab-item"
          :class="{ active: item.key === activeTab }"
          @click="onTabClick(item.key)"
        >
          <span class="tab-label text-sm">{{ item.label }}</span>
          <span v-if="item.count !== undefined" class="text-[10px]">
            {{ item.count }}
          </span>
          <svg
            v-if="item.count === undefined"
            class="animate-spin text-gray-400 w-3 h-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      </div>

      <transition name="carousel-fade">
        <button
          v-if="canScrollRight"
          class="carousel-arrow carousel-arrow--right"
          @click="scrollTabs(1)"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 18l6-6-6-6"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </transition>
    </div>
    <div id="customize-tabs" class="absolute top-full ltr:left-0 rtl:right-0">
      <CustomizeTabsPanel
        v-if="isCustomizePanelOpen"
        :groups="customizeGroups"
        @select-group="onSelectGroup"
        @toggle-item="onToggleItem"
        @reorder="onReorder"
      />
    </div>
  </div>
</template>
<script>
import wootConstants from 'dashboard/constants/globals';
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import uiSettingsMixin, {
  CONVERSATION_LIST_TAB_GROUPS,
} from 'dashboard/mixins/uiSettings';
import rtlMixin from 'shared/mixins/rtlMixin';
import CustomizeTabsPanel from './CustomizeTabsPanel.vue';

export default {
  components: { CustomizeTabsPanel },
  mixins: [uiSettingsMixin, clickaway, rtlMixin],
  inject: ['chatList'],
  props: {
    activeTab: {
      type: String,
      default: wootConstants.ASSIGNEE_TYPE.ME,
    },
    disabledSwitchGroup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isCustomizePanelOpen: false,
      canScrollLeft: false,
      canScrollRight: false,
    };
  },
  computed: {
    ...mapGetters({
      conversationStats: 'conversationStats/getStats',
      statsLoading: 'conversationStats/getIsLoading',
      allConversationsAllowed: 'getAllConversationsAllowed',
      inboxPermissions: 'getInboxPermissions',
    }),
    assigneeTabItems() {
      const ASSIGNEE_TYPE_TAB_KEYS = {
        me: 'mineCount',
        unassigned: 'unAssignedCount',
        all: 'allCount',
        new: 'newCount',
      };
      return (
        Object.keys(ASSIGNEE_TYPE_TAB_KEYS)
          // only admin can see all conversations (todo perhaps it is temp logic) //d99d
          .filter(
            key =>
              (key !== 'all' && key !== 'unassigned') ||
              this.allConversationsAllowed ||
              this.chatList.teamId ||
              this.inboxPermissions.find(
                permission => permission.name === 'Inbox read all conversations'
              )?.ids
          )
          .map(key => {
            const count = this.statsLoading
              ? undefined
              : this.conversationStats[ASSIGNEE_TYPE_TAB_KEYS[key]];
            return {
              key,
              name: this.$t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
              count,
            };
          })
      );
    },
    statusTabOptions() {
      const STATUS_COUNT_KEYS = {
        '!resolved': 'activeStatusCount',
        resolved: 'resolvedStatusCount',
        pending: 'pendingStatusCount',
        snoozed: 'snoozedStatusCount',
        all: 'allStatusCount',
      };
      const items = this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS');
      return Object.keys(items).map(key => ({
        key,
        name: items[key].TEXT,
        count: this.statsLoading
          ? undefined
          : this.conversationStats[STATUS_COUNT_KEYS[key]],
      }));
    },
    customizeGroups() {
      return [
        {
          key: CONVERSATION_LIST_TAB_GROUPS.OWNERSHIP,
          label: this.$t('CHAT_LIST.CUSTOMIZE_TABS.GROUPS.ownershipOptions'),
          selected:
            this.conversationListTabGroup ===
            CONVERSATION_LIST_TAB_GROUPS.OWNERSHIP,
          items: this.orderItems(
            this.assigneeTabItems,
            this.ownershipTabsOrder
          ),
        },
        {
          key: CONVERSATION_LIST_TAB_GROUPS.STATUS,
          label: this.$t('CHAT_LIST.CUSTOMIZE_TABS.GROUPS.statusOptions'),
          selected:
            this.conversationListTabGroup ===
            CONVERSATION_LIST_TAB_GROUPS.STATUS,
          items: this.orderItems(this.statusTabOptions, this.statusTabsOrder),
        },
      ];
    },
    activeTabItems() {
      if (this.disabledSwitchGroup) {
        const ownershipGroup = this.customizeGroups.find(
          group => group.key === CONVERSATION_LIST_TAB_GROUPS.OWNERSHIP
        );
        return ownershipGroup.items.filter(item => item.visible);
      }
      const activeGroup = this.customizeGroups.find(group => group.selected);
      return activeGroup.items.filter(item => item.visible);
    },
  },
  watch: {
    activeTabItems() {
      this.$nextTick(this.updateScrollState);
    },
  },
  mounted() {
    this.$nextTick(this.updateScrollState);
  },
  methods: {
    updateScrollState() {
      const el = this.$refs.tabsContainer;
      if (!el) return;
      if (this.isRTLView) {
        this.canScrollLeft = el.scrollLeft > el.clientWidth - el.scrollWidth + 1;
        this.canScrollRight = el.scrollLeft < -1;
      } else {
        this.canScrollLeft = el.scrollLeft > 0;
        this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      }
    },
    scrollTabs(direction) {
      const el = this.$refs.tabsContainer;
      if (!el) return;
      el.scrollBy({ left: direction * 120, behavior: 'smooth' });
    },
    orderItems(items, order) {
      return order
        .map(({ key, visible }) => {
          const item = items.find(i => i.key === key);
          return item
            ? { key, label: item.name, count: item.count, visible }
            : null;
        })
        .filter(Boolean);
    },
    togglePanel() {
      if (this.disabledSwitchGroup) return;
      this.isCustomizePanelOpen = !this.isCustomizePanelOpen;
    },
    closePanel() {
      this.isCustomizePanelOpen = false;
    },
    onTabClick(key) {
      if (key !== this.activeTab) {
        this.$emit('update-tab', {
          group: this.conversationListTabGroup,
          key,
        });
        if (
          this.conversationListTabGroup === CONVERSATION_LIST_TAB_GROUPS.STATUS
        ) {
          this.$store.dispatch('setChatStatusFilter', key);
        }
      }
    },
    onSelectGroup(group) {
      if (group !== this.conversationListTabGroup) {
        this.$store.dispatch('conversationStats/startLoading');
        this.updateConversationListTabGroup(group);
        window.bus.$emit('fetch_conversation_stats');
      }
    },
    onToggleItem(groupKey, itemKey) {
      const order =
        groupKey === CONVERSATION_LIST_TAB_GROUPS.STATUS
          ? this.statusTabsOrder
          : this.ownershipTabsOrder;
      const target = order.find(item => item.key === itemKey);
      const visibleCount = order.filter(item => item.visible).length;
      if (target.visible && visibleCount <= 1) {
        return;
      }
      const newOrder = order.map(item =>
        item.key === itemKey ? { ...item, visible: !item.visible } : item
      );
      this.updateConversationListTabsOrder(groupKey, newOrder);
      this.syncActiveTabIfHidden(groupKey, newOrder);
    },
    onReorder(groupKey, items) {
      const newOrder = items.map(({ key, visible }) => ({ key, visible }));
      this.updateConversationListTabsOrder(groupKey, newOrder);
    },
    syncActiveTabIfHidden(groupKey, order) {
      if (groupKey !== this.conversationListTabGroup) {
        return;
      }
      const activeItem = order.find(item => item.key === this.activeTab);
      if (activeItem && activeItem.visible) {
        return;
      }
      const firstVisible = order.find(item => item.visible);
      if (firstVisible) {
        this.$emit('update-tab', { group: groupKey, key: firstVisible.key });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.tab-customize-button {
  @apply flex items-center justify-center min-w-[32px] min-h-[32px] rounded-[4px] border border-neutral-100 cursor-pointer;

  &.active,
  &:hover:not(.disabled) {
    @apply bg-white border-gray-200 text-gray-600;
  }

  &.disabled {
    @apply cursor-not-allowed;
  }
}
.tabs-carousel {
  @apply relative flex-1 min-w-0 flex items-center;
}
.tabs-scroll-container {
  @apply flex flex-row items-center gap-1.5 overflow-x-auto flex-1 min-w-0;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
.carousel-arrow {
  @apply absolute top-1/2 -translate-y-1/2 z-10;
  @apply flex items-center justify-center w-5 h-full;
  @apply bg-white rounded-[4px] shadow-sm;
  @apply cursor-pointer text-gray-600;
  padding: 0;
  border: 1px solid #e5e7eb;

  &:hover {
    @apply border-gray-300 text-gray-800;
  }

  &--left {
    @apply -left-1;
  }

  &--right {
    @apply -right-1;
  }
}
.carousel-fade-enter-active,
.carousel-fade-leave-active {
  transition: opacity 0.15s ease;
}
.carousel-fade-enter,
.carousel-fade-leave-to {
  opacity: 0;
}
.tab-item {
  @apply flex flex-row items-center gap-1 cursor-pointer shrink-0;
  @apply text-gray-500 border border-neutral-100 rounded-[4px] px-2 py-1.5;

  &.active,
  &:hover {
    @apply bg-white border-gray-200;

    .tab-label {
      @apply text-gray-600;
    }
  }
}
</style>
